import {Box, Button, DialogActions, DialogContent, Stack, Typography} from '@mui/material';
import {
  type GlobalSettingsShortTermGrowthModelTypeEnum,
  TenantShortTermGrowthModelTypeEnum,
  type GlobalSettings,
} from 'client';
import DialogTitleWithClose from 'components/Generic/DialogTitle';
import GenericDialog from 'components/Generic/GenericDialog';
import {SingleSelect} from 'components/Shared/SingleSelect';
import {EMPTY_ID, useGetGlobalSettings} from 'hooks/swr';
import {SWRKeyEnum, type ModalDialogProps} from 'interfaces';
import {useEffect, useState, type FC} from 'react';
import {useTranslation} from 'react-i18next';
import backendCommunicator from 'services/backendCommunicator';
import {mutate} from 'swr';
import {LoadingOverlay} from 'components/Loading';

export const GlobalSettingsModal: FC<ModalDialogProps<any, {id?: string}>> = ({dialogProps}) => {
  const {t} = useTranslation('common');
  const {data: settings, error} = useGetGlobalSettings({resourceId: EMPTY_ID});
  const [globalSettings, setGlobalSettings] = useState<GlobalSettings | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (settings) {
      setGlobalSettings(settings);
    }
  }, [settings]);

  if (!error && !globalSettings) {
    return <LoadingOverlay />;
  }

  const closeModal = () => dialogProps.onClose && dialogProps.onClose({}, 'escapeKeyDown');
  return (
    <GenericDialog {...dialogProps} data-testid="global-settings-modal">
      <DialogTitleWithClose
        onClose={() => {
          closeModal();
        }}
      >
        {t('globalSettings')}
      </DialogTitleWithClose>
      <DialogContent sx={{padding: 1}}>
        {globalSettings && (
          <Stack>
            <Stack direction={'row'} justifyContent="space-between" alignItems="baseline" spacing={3} p={2} pt={0}>
              <Stack spacing={1}>
                <Typography variant="h6">{t('planSettingsShortTermModelType')}</Typography>
                <Typography variant="caption" color="text.disabled">
                  {t('planSettingsShortTermModelTypeCaption')}
                </Typography>
              </Stack>
              <Box display="flex" justifyContent="flex-end" minWidth={200}>
                <SingleSelect
                  onClick={(shortTermGrowthModelType) => {
                    setGlobalSettings({...globalSettings, shortTermGrowthModelType});
                  }}
                  selectedKey={globalSettings.shortTermGrowthModelType}
                  options={
                    new Map<GlobalSettingsShortTermGrowthModelTypeEnum, string>([
                      [TenantShortTermGrowthModelTypeEnum.SimpleEwma, t('simple_ewma')],
                      [TenantShortTermGrowthModelTypeEnum.DynamicEwma, t('dynamic_ewma')],
                      [TenantShortTermGrowthModelTypeEnum.NgrResidualEts, t('ngr_residual_ets')],
                    ])
                  }
                />
              </Box>
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          disabled={!settings || isLoading}
          autoFocus
          variant="contained"
          onClick={async () => {
            if (globalSettings) {
              setIsLoading(true);
              await mutate(
                [EMPTY_ID, SWRKeyEnum.GET_GLOBAL_SETTINGS],
                backendCommunicator
                  .updateGlobalSettings(globalSettings)
                  .then(() => closeModal())
                  .finally(() => {
                    setIsLoading(false);
                  }),
              );
            }
          }}
          loading={isLoading}
          loadingPosition="end"
        >
          {t('save')}
        </Button>
      </DialogActions>
    </GenericDialog>
  );
};
